import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./analyse&document.css";
import laserScanImage from "./images/Laserscan.jpg";
import photogrammetryImage from "./images/Photogrammetrie.jpg";
import infraredImage from "./images/Infrarot.jpg";
import ctScanImage from "./images/CT.jpg";
import thicknessAnalysisImage from "./images/Staerkenanalyse.jpg";
import stressAnalysisImage from "./images/Spannungsanalyse.jpg";
import documentationBg from "./images/Dokumentation.JPG";
import goDownIcon from "./images/GoDown.png";
import VideoTiles from "./VideoTiles";

// Import translations
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const AnalyseDocument = () => {
  const { t, i18n } = useTranslation();

  // Add translations for this component
  i18n.addResourceBundle("en", "analyseDocument", enTranslations, true, true);
  i18n.addResourceBundle("de", "analyseDocument", deTranslations, true, true);

  // Sketchfab consent management
  const [sketchfabConsent, setSketchfabConsent] = useState(() => {
    // Check localStorage for existing consent
    const savedConsent = localStorage.getItem("sketchfab_consent");
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");

    // If user accepted cookies via banner, automatically allow Sketchfab
    if (cookiesAccepted === "true") {
      return true;
    }

    return savedConsent === "true";
  });
  const [showDeclineMessage, setShowDeclineMessage] = useState(() => {
    // Check if user previously declined
    const savedConsent = localStorage.getItem("sketchfab_consent");
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");

    // If cookies were accepted, don't show decline message
    if (cookiesAccepted === "true") {
      return false;
    }

    return savedConsent === "false";
  });
  const [analyzeActiveTab, setAnalyzeActiveTab] = useState("stress_analysis");
  const [documentationActiveTab, setDocumentationActiveTab] =
    useState("infrared");
  const [visibleModels, setVisibleModels] = useState([0]);

  // Listen for cookie acceptance to automatically enable Sketchfab
  useEffect(() => {
    const handleStorageChange = () => {
      const cookiesAccepted = localStorage.getItem("cookiesAccepted");
      if (cookiesAccepted === "true") {
        setSketchfabConsent(true);
        setShowDeclineMessage(false);
      }
    };

    // Listen for storage changes (when cookie banner updates localStorage)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case storage event doesn't fire (same tab)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const analyzeTabOptions = ["thickness_analysis", "stress_analysis"];
  const analysisImages = {
    thickness_analysis: thicknessAnalysisImage,
    stress_analysis: stressAnalysisImage,
  };
  const documentationTabOptions = [
    "laser_scan",
    "photogrammetry",
    "infrared",
    "ct_scan",
  ];

  const documentationImages = {
    laser_scan: { src: laserScanImage, width: 2939, height: 1596 },
    photogrammetry: { src: photogrammetryImage, width: 2677, height: 1511 },
    infrared: { src: infraredImage, width: 4908, height: 2878 },
    ct_scan: { src: ctScanImage, width: 2804, height: 1735 },
  };

  const modelData = [
    {
      src: "https://sketchfab.com/models/a7bb6203f6964376bf7ba7577694303f/embed?autostart=1&ui_controls=0&ui_infos=0&ui_inspector=0&ui_stop=0&ui_watermark=0&ui_watermark_link=0",
      title: t("analyseDocument:model_1_title"),
      description: t("analyseDocument:model_1_desc"),
    },
    {
      src: "https://sketchfab.com/models/a9e1c1299db841e880ffe7c628c69a11/embed?autostart=1&ui_controls=0&ui_infos=0&ui_inspector=0&ui_stop=0&ui_watermark=0&ui_watermark_link=0",
      title: t("analyseDocument:model_2_title"),
      description: t("analyseDocument:model_2_desc"),
    },
    {
      src: "https://sketchfab.com/models/aca11470877942fb83e548f8e1d25be2/embed?autostart=1&ui_controls=0&ui_infos=0&ui_inspector=0&ui_stop=0&ui_watermark=0&ui_watermark_link=0",
      title: t("analyseDocument:model_3_title"),
      description: t("analyseDocument:model_3_desc"),
    },
  ];

  // Handle Sketchfab consent
  const handleSketchfabConsent = (consent) => {
    if (consent) {
      localStorage.setItem("sketchfab_consent", "true");
      setSketchfabConsent(true);
      setShowDeclineMessage(false);
    } else {
      // Save decline choice to localStorage
      localStorage.setItem("sketchfab_consent", "false");
      setSketchfabConsent(false);
      setShowDeclineMessage(true);
    }
  };

  // Handle returning to consent choice
  const handleReturnToConsent = () => {
    setShowDeclineMessage(false);
    setSketchfabConsent(false);
  };

  // Sketchfab decline message component
  const SketchfabDeclineMessage = () => (
    <div className="sketchfab-consent-placeholder">
      <div className="consent-content">
        <p>
          {t(
            "analyseDocument:decline_message",
            "Sketchfab content was not loaded for data protection reasons."
          )}
        </p>
        <div className="consent-buttons">
          <button
            className="consent-btn consent-accept"
            onClick={handleReturnToConsent}
          >
            {t("analyseDocument:reconsider", "Reconsider choice")}
          </button>
        </div>
      </div>
    </div>
  );

  // Sketchfab consent placeholder component
  const SketchfabConsentPlaceholder = ({ title, description }) => (
    <div className="sketchfab-consent-placeholder">
      <div className="consent-content">
        <p>
          {t(
            "analyseDocument:consent_message",
            "To display this 3D model, we need to load content from Sketchfab. This may transfer data to external servers."
          )}
        </p>
        <div className="consent-buttons">
          <button
            className="consent-btn consent-accept"
            onClick={() => handleSketchfabConsent(true)}
          >
            {t("analyseDocument:consent_accept", "Load all 3D models")}
          </button>
          <button
            className="consent-btn consent-decline"
            onClick={() => handleSketchfabConsent(false)}
          >
            {t("analyseDocument:consent_decline", "Keep blocked")}
          </button>
        </div>
      </div>
    </div>
  );

  const showNextModel = () => {
    if (visibleModels.length < modelData.length) {
      setVisibleModels([...visibleModels, visibleModels.length]);
    }
  };

  // Add scroll effect for light reflections
  useEffect(() => {
    const handleScroll = () => {
      const modelsSection = document.querySelector(
        ".additional-models-section"
      );
      if (modelsSection) {
        const scrollTop = window.pageYOffset;
        const sectionTop = modelsSection.offsetTop;
        const sectionHeight = modelsSection.offsetHeight;
        const progress = Math.max(
          0,
          Math.min(
            1,
            (scrollTop - sectionTop + window.innerHeight) /
              (sectionHeight + window.innerHeight)
          )
        );

        modelsSection.style.setProperty("--scroll-progress", progress);

        // Apply the same reflection effect to each model item
        document.querySelectorAll(".model-item").forEach((el) => {
          el.style.setProperty("--scroll-progress", progress);
        });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="analyse-document-page">
      {/* Analyse Section */}
      <div className="analyse-section">
        <div className="section-title">
          <h1
            className="focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={t(
              "analyseDocument:analyse_title_aria",
              "Seitentitel: {{title}}",
              { title: t("analyseDocument:analyse_title") },
            )}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("analyseDocument:analyse_title")}
          </h1>
        </div>

        <div className="content-container">
          <div className="model-container">
            <div className="sketchfab-embed-wrapper">
              {sketchfabConsent ? (
                <iframe
                  title={t("analyseDocument:main_model_desc")}
                  frameBorder="0"
                  allowFullScreen
                  src="https://sketchfab.com/models/881102038ff241348d7ae88208caa79a/embed?autostart=1&ui_controls=0&ui_infos=0&ui_inspector=0&ui_stop=0&ui_watermark=0&ui_watermark_link=0"
                ></iframe>
              ) : showDeclineMessage ? (
                <SketchfabDeclineMessage />
              ) : (
                <SketchfabConsentPlaceholder
                  title={t("analyseDocument:main_model_desc")}
                  description={t(
                    "analyseDocument:main_model_desc",
                    "Interactive 3D analysis model",
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analysis images and separate explanatory captions */}
      <div className="analyze-tabs-section">
        <div className="tabs-container">
          {analyzeTabOptions.map((tab) => (
            <div
              key={tab}
              className={`expandable-tab method-tab analysis-tab ${
                analyzeActiveTab === tab ? "active" : ""
              }`}
            >
              <button
                type="button"
                id={`${tab}-button`}
                className="tab-label method-tab-button focus-highlight"
                onClick={() => setAnalyzeActiveTab(tab)}
                aria-expanded={analyzeActiveTab === tab}
                aria-controls={`${tab}-panel`}
              >
                <span>{t(`analyseDocument:${tab}`)}</span>
              </button>
              <figure
                id={`${tab}-panel`}
                className="method-panel"
                aria-labelledby={`${tab}-button`}
                hidden={analyzeActiveTab !== tab}
              >
                <ResponsiveImage sizes="100vw"
                  src={analysisImages[tab]}
                  alt={t(`analyseDocument:${tab}_image_alt`)}
                  width={tab === "thickness_analysis" ? 2830 : 2818}
                  height={tab === "thickness_analysis" ? 1528 : 1539}
                  loading="lazy"
                />
                <figcaption className="method-description">
                  <h3>{t(`analyseDocument:${tab}`)}</h3>
                  <p>{t(`analyseDocument:${tab}_description`)}</p>
                </figcaption>
              </figure>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation image and introduction */}
      <section className="documentation-intro">
        <div className="documentation-bg-image-container">
          <ResponsiveImage sizes="100vw"
            src={documentationBg}
            alt={t("analyseDocument:documentation_image_alt")}
            width={5184}
            height={2406}
            loading="lazy"
            className="documentation-bg-image"
          />
          <div className="documentation-title-overlay">
            <h1
              className="focus-highlight"
              tabIndex={0}
              role="text"
              aria-label={t(
                "analyseDocument:documentation_title_aria",
                "Seitentitel: {{title}}",
                { title: t("analyseDocument:documentation_title") },
              )}
              style={{ pointerEvents: "none" }}
              onFocus={(e) => {
                e.target.style.pointerEvents = "auto";
              }}
              onBlur={(e) => {
                e.target.style.pointerEvents = "none";
              }}
            >
              {t("analyseDocument:documentation_title")}
            </h1>
          </div>
        </div>
        <div className="method-description">
          <p>{t("analyseDocument:documentation_description")}</p>
        </div>
      </section>

      {/* Documentation Section */}
      <div className="documentation-section">
        <div className="content-container">
          <div className="tabs-container">
            {documentationTabOptions.map((tab) => (
              <div
                key={tab}
                className={`expandable-tab method-tab ${
                  documentationActiveTab === tab ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  id={`${tab}-button`}
                  className="tab-label method-tab-button focus-highlight"
                  onClick={() => setDocumentationActiveTab(tab)}
                  aria-expanded={documentationActiveTab === tab}
                  aria-controls={`${tab}-panel`}
                >
                  <span>{t(`analyseDocument:${tab}`)}</span>
                </button>
                <figure
                  id={`${tab}-panel`}
                  className="method-panel"
                  aria-labelledby={`${tab}-button`}
                  hidden={documentationActiveTab !== tab}
                >
                  <ResponsiveImage sizes="100vw"
                    src={documentationImages[tab].src}
                    alt={t(`analyseDocument:${tab}`)}
                    width={documentationImages[tab].width}
                    height={documentationImages[tab].height}
                    loading="lazy"
                  />
                  <figcaption className="method-description">
                    <h3>{t(`analyseDocument:${tab}`)}</h3>
                    <p>{t(`analyseDocument:${tab}_description`)}</p>
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
        </div>
      </div>

      <VideoTiles />

      {/* Additional 3D Models Section */}
      <div className="additional-models-section">
        <div className="models-section-title">
          <h1
            className="focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={t(
              "analyseDocument:models_title_aria",
              "Seitentitel: {{title}}",
              { title: t("analyseDocument:models_title") },
            )}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("analyseDocument:models_title")}
          </h1>
        </div>
        <div className="models-grid">
          {visibleModels.map((modelIndex) => (
            <div key={modelIndex} className="model-item-wrapper">
              <div className="model-item">
                <div className="sketchfab-embed-wrapper">
                  {sketchfabConsent ? (
                    <iframe
                      title={modelData[modelIndex].title}
                      frameBorder="0"
                      allowFullScreen
                      src={modelData[modelIndex].src}
                    ></iframe>
                  ) : showDeclineMessage ? (
                    <SketchfabDeclineMessage />
                  ) : (
                    <SketchfabConsentPlaceholder
                      title={modelData[modelIndex].title}
                      description={modelData[modelIndex].description}
                    />
                  )}
                </div>
              </div>
              <div className="model-description">
                <h2
                  className="focus-highlight"
                  tabIndex={0}
                  role="text"
                  aria-label={t(
                    "analyseDocument:model_desc_aria",
                    "Modellbeschreibung: {{description}}",
                    { description: modelData[modelIndex].description },
                  )}
                  style={{ pointerEvents: "none" }}
                  onFocus={(e) => {
                    e.target.style.pointerEvents = "auto";
                  }}
                  onBlur={(e) => {
                    e.target.style.pointerEvents = "none";
                  }}
                >
                  {modelData[modelIndex].description}
                </h2>
                {modelIndex < modelData.length - 1 &&
                  modelIndex === visibleModels.length - 1 && (
                    <button className="expand-button2" onClick={showNextModel}>
                      <img
                        src={goDownIcon}
                        alt={t("analyseDocument:show_more")}
                      />
                    </button>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyseDocument;
